import { useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { REVIEW_CPM_RANKING_CLIP, type RankingReport } from '@/queries/cpmRanking';
import { isValidRankingClipReason } from '@/lib/campaigns/rankingBulkReview';
import { isManualFacebookReport } from '@/lib/campaigns/manualFacebookReport';

export type RankingClipDecision = 'approved' | 'disapproved' | 'neutral';
const sessionToken = () => { try { return localStorage.getItem('token'); } catch { return null; } };

/** One in-flight decision per mounted row; stale sessions never refresh a new account. */
export function useRankingClipReview({ report, campaignId, readOnly, phase = '' }: {
  report: RankingReport; campaignId: string; readOnly?: boolean; phase?: string;
}) {
  const client = useApolloClient();
  const [reason, setReason] = useState('');
  const [ownership, setOwnership] = useState(false);
  const [decision, setDecision] = useState<RankingClipDecision | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  const token = sessionToken();
  const scope = JSON.stringify([token, campaignId, report.submission_id, phase, !!readOnly]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const mounted = useRef(true);
  const request = useRef(0);
  const locked = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; request.current += 1; };
  }, []);
  useEffect(() => {
    request.current += 1; locked.current = false;
    setPending(false); setError(false); setReason(''); setOwnership(false); setDecision(null);
  }, [scope]);
  // Only a Facebook clip without a connected Page needs the reviewer's ownership confirmation.
  const manualFacebook = isManualFacebookReport(report);
  const ownershipConfirmed = manualFacebook ? ownership : report.ownership_verified === true;
  const canReturnToNeutral = !!report.submission_id && ['approved', 'disapproved'].includes(report.clip_approval ?? '');
  const reasonValid = isValidRankingClipReason(reason);
  const dismiss = () => { if (!locked.current) { setDecision(null); setReason(''); setOwnership(false); setError(false); } };
  const save = async (approval: RankingClipDecision, refresh: () => Promise<unknown>) => {
    if (!mounted.current || currentScope.current !== scope || sessionToken() !== token) return;
    if (locked.current || readOnly || !report.submission_id || (approval === 'neutral' ? !canReturnToNeutral : !reasonValid) || (approval === 'approved' && !ownershipConfirmed)) return;
    const ticket = ++request.current;
    const isCurrent = () => mounted.current && request.current === ticket && currentScope.current === scope && sessionToken() === token;
    locked.current = true; setPending(true); setError(false);
    try {
      const result = await client.mutate({ mutation: REVIEW_CPM_RANKING_CLIP, fetchPolicy: 'no-cache', errorPolicy: 'all', variables: {
        id: report.submission_id, approval,
        ...(approval !== 'neutral' && reason.trim() ? { reason: reason.trim() } : {}),
        ...(manualFacebook && approval === 'approved' ? { ownershipVerified: true } : {}),
      } });
      if (!isCurrent()) return;
      if (result.errors?.length || ('error' in result && result.error) || result.data?.adminReviewCpmRankingClip?.submission_id !== report.submission_id) throw new Error('clip_review_failed');
      await refresh();
      if (isCurrent()) { setDecision(null); setReason(''); setOwnership(false); }
    } catch {
      if (isCurrent()) { setError(true); setDecision(approval); }
    } finally {
      if (isCurrent()) { locked.current = false; setPending(false); }
    }
  };
  return { reason, setReason, ownership, setOwnership, decision, setDecision, pending, error, reasonValid, canReturnToNeutral, ownershipConfirmed, manualFacebook, dismiss, save };
}
